import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, BarChart3 } from 'lucide-react';
import { User } from '../../models/User';
import { COUNTRIES } from '../../data/userDatabase.ts';
import { 
  Shareholder, 
  getShareholders, 
  addShareholder, 
  updateShareholder, 
  deleteShareholder 
} from '../../data/capTableDatabase';

interface AdminCapTableProps {
  user: User;
}

const AdminCapTable: React.FC<AdminCapTableProps> = ({ user }) => {
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Shareholder, 'id'>>({
    shareholderName: '',
    country: '',
    companyIndividual: 'Individual',
    shareholderSince: '',
    shares: 0,
    hoasShares: 0,
    entity: 'HOH'
  });

  useEffect(() => {
    loadShareholders();
  }, []);

  const loadShareholders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const shareholdersData = await getShareholders();
      setShareholders(shareholdersData);
    } catch (err) {
      console.error('Error loading shareholders:', err);
      setError('Failed to load shareholders. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'shares' || name === 'hoasShares' ? parseInt(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.shareholderName.trim()) {
              // Shareholder name is required
      return;
    }

    try {
      if (editingId) {
        const updatedShareholder = { ...formData, id: editingId };
        await updateShareholder(updatedShareholder);
        setShareholders(prev => prev.map(s => s.id === editingId ? updatedShareholder : s));
      } else {
        const newShareholder = await addShareholder(formData);
        setShareholders(prev => [...prev, newShareholder]);
      }
      
      resetForm();
    } catch (err) {
      console.error('Error saving shareholder:', err);
              // Failed to save shareholder
    }
  };

  const handleEdit = (shareholder: Shareholder) => {
    setFormData({
      shareholderName: shareholder.shareholderName,
      country: shareholder.country,
      companyIndividual: shareholder.companyIndividual,
      shareholderSince: shareholder.shareholderSince,
      shares: shareholder.shares,
      hoasShares: shareholder.hoasShares,
      entity: shareholder.entity
    });
    setEditingId(shareholder.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this shareholder? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteShareholder(id);
      setShareholders(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting shareholder:', err);
              // Failed to delete shareholder
    }
  };

  const resetForm = () => {
    setFormData({
      shareholderName: '',
      country: '',
      companyIndividual: 'Individual',
      shareholderSince: '',
      shares: 0,
      hoasShares: 0,
      entity: 'HOH'
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={loadShareholders}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Shareholder Management</h3>
          <p className="text-sm text-gray-600">Manage cap table data and shareholder information</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Shareholder
        </button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-medium text-gray-900">
              {editingId ? 'Edit Shareholder' : 'Add New Shareholder'}
            </h4>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shareholder Name *
                </label>
                <input
                  type="text"
                  name="shareholderName"
                  value={formData.shareholderName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Country</option>
                  {COUNTRIES.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company/Individual
                </label>
                <select
                  name="companyIndividual"
                  value={formData.companyIndividual}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Individual">Individual</option>
                  <option value="Company">Company</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shareholder Since
                </label>
                <input
                  type="date"
                  name="shareholderSince"
                  value={formData.shareholderSince}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Shares
                </label>
                <input
                  type="number"
                  name="shares"
                  value={formData.shares}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entity
                </label>
                <select
                  name="entity"
                  value={formData.entity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="HOH">HOH</option>
                  <option value="HOAS">HOAS</option>
                </select>
              </div>

              {formData.entity === 'HOAS' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    HOAS Shares
                  </label>
                  <input
                    type="number"
                    name="hoasShares"
                    value={formData.hoasShares}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Update' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Shareholders Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Shareholders</h4>
          
          {shareholders.length === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No shareholders found</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-2 text-blue-600 hover:text-blue-700"
              >
                Add your first shareholder
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shareholder Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Since
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      HOAS Shares
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Shares
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {shareholders.map((shareholder) => (
                    <tr key={shareholder.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {shareholder.shareholderName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {shareholder.country}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          shareholder.companyIndividual === 'Company' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {shareholder.companyIndividual}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {shareholder.shareholderSince ? new Date(shareholder.shareholderSince).toISOString().split('T')[0] : ''}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {(shareholder.hoasShares || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {(shareholder.shares || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          shareholder.entity === 'HOH' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {shareholder.entity}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(shareholder)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(shareholder.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Summary Statistics */}
      {shareholders.length > 0 && (
        <div className="bg-white shadow rounded-lg p-6">
          <h4 className="text-lg font-medium text-gray-900 mb-4">Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{shareholders.length}</div>
              <div className="text-sm text-gray-600">Total Shareholders</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {shareholders.reduce((sum, s) => sum + (s.shares || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Shares</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {shareholders.reduce((sum, s) => sum + (s.hoasShares || 0), 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total HOAS Shares</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {shareholders.filter(s => s.entity === 'HOH').length} / {shareholders.filter(s => s.entity === 'HOAS').length}
              </div>
              <div className="text-sm text-gray-600">HOH / HOAS</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCapTable; 